import React, { useState } from 'react';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import TextareaAutosize  from '@mui/material/TextareaAutosize';
import Divider from '@mui/material/Divider';
import EqualizerIcon from '@mui/icons-material/InsertChartOutlined';
import { ComposedChart, Scatter, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

import { AutoSaveProvider, useAutoSaveState, SaveButton, NameField } from '../lib/useAutoSaveState'
import { quadraticRegression } from '../lib/stats'
import toFixed from '../lib/toFixed'

function QuadraticRegression () {
  const [groupOneLabel, setGroupOneLabel] = useAutoSaveState('groupOneLabel', 'X')
  const [groupTwoLabel, setGroupTwoLabel] = useAutoSaveState('groupTwoLabel', 'Y')
  const [data1, setData1] = useAutoSaveState('data1', '')
  const [data2, setData2] = useAutoSaveState('data2', '')
  const [ preview, setPreview ] = useAutoSaveState('preview', null)
  const [result, setResult] = useState({})
  const [points, setPoints] = useState([])
  const { a, b, c, rSquared } = result

  const calculate = () => {
    const input1 = data1.split(/[^0-9.-]/).filter(v => v).map(v => parseFloat(v))
    const input2 = data2.split(/[^0-9.-]/).filter(v => v).map(v => parseFloat(v))
    const points = input1.map((x, i) => ({ x, y: input2[i] }))
    console.log(points)
    const output = quadraticRegression(points)
    output.complete = true
    console.log({ output })
    setPoints(points)
    setResult(output)
    setPreview(`a: ${toFixed(output.a, 5)}, b: ${toFixed(output.b, 5)}, c: ${toFixed(output.c, 5)}, R²: ${toFixed(output.rSquared, 5)}`)
    window.gtag('event', 'calculate-quadratic-regression')
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Grid container spacing={3}>
        <Grid container justifyContent="center" xs={12} style={{ position: 'relative' }}>
          <Typography variant="h2" component="h1" gutterBottom>
            Quadratic Regression
          </Typography>
          <SaveButton />
        </Grid>
        <NameField />
        <Grid item xs={6}>
          <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  name="groupOneLabel"
                  fullWidth
                  id="groupOneLabel"
                  label="X Values"
                  autoFocus
                  value={groupOneLabel}
                  onChange={e => setGroupOneLabel(e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body1">Values</Typography>
                <TextareaAutosize
                  style={{ width: '100%' }}
                  aria-label="data"
                  minRows={3}
                  maxRows={20}
                  placeholder="Enter data..."
                  value={data1}
                  onChange={e => setData1(e.target.value)}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        <Grid item xs={6}>
          <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  name="groupTwoLabel"
                  fullWidth
                  id="groupTwoLabel"
                  label="Y Values"
                  value={groupTwoLabel}
                  onChange={e => setGroupTwoLabel(e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body1">Values</Typography>
                <TextareaAutosize
                  style={{ width: '100%' }}
                  aria-label="data"
                  minRows={3}
                  maxRows={20}
                  placeholder="Enter data..."
                  value={data2}
                  onChange={e => setData2(e.target.value)}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        <Grid item xs={12}>
          <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
            <Grid container xs={12} justifyContent="center">
              <Button variant="contained" onClick={calculate}>Calculate</Button>
            </Grid>
          </Paper>
        </Grid>
        {result.complete  && <Grid item xs={12}>
          <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
            <Grid container xs={12} justifyContent="center">
              <Typography variant="h5" gutterBottom>
                <EqualizerIcon /> Quadratic Regression Results
              </Typography>
            </Grid>
            <Divider style={{ marginBottom: '20px', marginTop: '20px' }} />
            <Box display="flex" justifyContent="space-around" mb={2}>
              <Typography variant="body1"><b>Coefficient of Determination (R²):</b> {toFixed(rSquared, 5)}</Typography>
            </Box>
            <Divider style={{ marginBottom: '20px' }} />
            <Box display="flex" justifyContent="space-around" mb={2}>
              <Typography variant="body1"><b>Equation:</b> y = {toFixed(a, 5)} x <sup>2</sup> + {toFixed(b, 5)} x + {toFixed(c, 5)}</Typography>
            </Box>
          </Paper>
        </Grid>}
        {result.complete && <Grid item xs={12}>
          <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
            <ScatterPlot data={points} coefficients={{ a, b, c }} />
          </Paper>
        </Grid>}
      </Grid>
    </Container>
  );
}

const ScatterPlot = ({ data, coefficients }) => {
  const sortedData = [...data].sort((a, b) => a.x - b.x);
  const firstPoint = sortedData[0];
  const lastPoint = sortedData[sortedData.length - 1];
  const xMin = Math.min(...data.map(v => v.x))
  const xMax = Math.max(...data.map(v => v.x))

  const linePoints = []
  for (let i = 0; i < data.length; i++) {
    linePoints.push({
      x: data[i].x,
      y: coefficients.a * data[i].x * data[i].x + coefficients.b * data[i].x + coefficients.c
    })
  }
    //{ x: firstPoint.x, y: regressionLine.m * firstPoint.x + regressionLine.b },
    //{ x: lastPoint.x, y: regressionLine.m * lastPoint.x + regressionLine.b }

  return (
    <ResponsiveContainer width="100%" height={400}>
      <ComposedChart
        margin={{
          top: 20, right: 20, bottom: 20, left: 20,
        }}
      >
        <CartesianGrid />
        <XAxis type="number" dataKey="x" name="X Value" domain={[ xMin, xMax ]} />
        <YAxis type="number" dataKey="y" name="Y Value" />
        <Tooltip cursor={{ strokeDasharray: '3 3' }} />
        <Scatter name="Data Points" data={data} fill="#8884d8" />
        <Line type="monotone" dataKey="y" data={linePoints} stroke="#ff7300" />
      </ComposedChart>
    </ResponsiveContainer>
  );
}

export default function WrappedQuadraticRegression () {
  return (
    <AutoSaveProvider>
      <QuadraticRegression />
    </AutoSaveProvider>
  );
}
